import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Tag,
  Eye,
  ListChecks,
  Target,
  Users,
  Trophy,
  Settings,
  Bell,
  Sparkles,
  HelpCircle,
  Bug,
  LogOut,
  LogIn,
  Medal,
  Award,
  Star,
  Diamond,
  X,
  // Admin icons
  LayoutDashboard,
  Server,
  Languages,
  Globe,
  Bot,
  Database,
  Brain,
  Share2,
  BarChart3,
  Package,
  Activity,
  UserCog,
  PieChart,
  Dices,
  type LucideIcon,
} from 'lucide-react';
import { useAuthStore } from '../../stores/auth-store';
import { useGamificationStore, LEVELS } from '../../stores/gamification-store';
import { BugReportModal } from './BugReportModal';
import LanguageSelector from './LanguageSelector';
import { useLanguage } from '../../contexts/LanguageContext';
import { PERMISSIONS } from '../../hooks/usePermission';

type MenuItem =
  | { type: 'divider' }
  | { type: 'link'; label: string; href: string; icon: React.ComponentType<{ className?: string }>; highlight?: boolean; danger?: boolean }
  | { type: 'button'; label: string; action: () => void; icon: React.ComponentType<{ className?: string }>; highlight?: boolean; danger?: boolean };

interface AdminNavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  permissions?: string[];
}

interface ProfileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ProfileMenu({ isOpen, onClose }: ProfileMenuProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef<HTMLDivElement>(null);
  const [bugReportOpen, setBugReportOpen] = useState(false);
  // Default expanded section based on current page
  const isOnAdminPage = location.pathname.startsWith('/admin');
  const [expandedSection, setExpandedSection] = useState<'navigation' | 'admin' | null>(
    isOnAdminPage ? 'admin' : 'navigation'
  );
  const { t } = useLanguage();

  const { isAuthenticated, user, logout, isAdmin, hasAnyPermission } = useAuthStore();
  const { level, xp, getLevelIcon, getXpProgress } = useGamificationStore();

  // Admin navigation items
  const operationsItems: AdminNavItem[] = [
    { name: t('adminNav.opsCenter', 'Operations Center'), href: '/admin/ops', icon: Activity, permissions: [PERMISSIONS.SEEDS_VIEW] },
    { name: t('adminNav.seedBrowsers', 'Seed Browsers'), href: '/admin/seeds', icon: Bot, permissions: [PERMISSIONS.SEEDS_VIEW] },
    { name: t('adminNav.storeBrowser', 'Store Browser'), href: '/admin/store-browser', icon: Database, permissions: [PERMISSIONS.STORES_VIEW] },
    { name: t('adminNav.errorInvestigation', 'Error Investigation'), href: '/admin/errors', icon: Bug, permissions: [PERMISSIONS.SEEDS_VIEW] },
  ];

  const analyticsItems: AdminNavItem[] = [
    { name: t('adminNav.dashboard', 'Dashboard'), href: '/admin', icon: LayoutDashboard, permissions: [PERMISSIONS.DASHBOARD_VIEW] },
    { name: t('adminNav.reports', 'Reports'), href: '/admin/reports', icon: BarChart3, permissions: [PERMISSIONS.REPORTS_VIEW] },
    { name: t('adminNav.userInsights', 'User Insights'), href: '/admin/user-insights', icon: PieChart, permissions: [PERMISSIONS.USERS_VIEW] },
    { name: t('adminNav.system', 'System'), href: '/admin/system', icon: Server, permissions: [PERMISSIONS.SYSTEM_VIEW] },
  ];

  const configItems: AdminNavItem[] = [
    { name: t('adminNav.users', 'Users'), href: '/admin/users', icon: UserCog, permissions: [PERMISSIONS.USERS_VIEW] },
    { name: t('adminNav.bugReports', 'Bug Reports'), href: '/admin/bug-reports', icon: Bug, permissions: [PERMISSIONS.SUPPORT_VIEW] },
    { name: t('adminNav.website', 'Website'), href: '/admin/website', icon: Globe, permissions: [PERMISSIONS.WEBSITE_VIEW] },
    { name: t('adminNav.translations', 'Translations'), href: '/admin/translations', icon: Languages, permissions: [PERMISSIONS.TRANSLATIONS_VIEW] },
    { name: t('adminNav.aiSettings', 'AI Settings'), href: '/admin/ai-settings', icon: Brain, permissions: [PERMISSIONS.AI_VIEW] },
    { name: t('adminNav.smcc', 'SMCC'), href: '/admin/smcc', icon: Share2, permissions: [PERMISSIONS.SMCC_VIEW] },
    { name: t('adminNav.slotMachines', 'Slot Machines'), href: '/admin/slot-skins', icon: Dices, permissions: [PERMISSIONS.SLOT_SKINS_VIEW] },
    { name: t('adminNav.devBuilds', 'Dev Builds'), href: '/admin/dev-builds', icon: Package },
    { name: t('adminNav.settings', 'Settings'), href: '/admin/settings', icon: Settings, permissions: [PERMISSIONS.SYSTEM_MANAGE] },
  ];

  // Filter admin items based on permissions
  const filterAdminItems = (items: AdminNavItem[]) => {
    return items.filter((item) => {
      if (!item.permissions || item.permissions.length === 0) return true;
      return hasAnyPermission(item.permissions);
    });
  };

  const filteredOperations = filterAdminItems(operationsItems);
  const filteredAnalytics = filterAdminItems(analyticsItems);
  const filteredConfig = filterAdminItems(configItems);
  const allAdminItems = [...filteredOperations, ...filteredAnalytics, ...filteredConfig];

  const isAdminItemActive = (item: AdminNavItem) => {
    if (item.href === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(item.href);
  };

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // Don't close if clicking inside the menu
      if (menuRef.current && menuRef.current.contains(target)) {
        return;
      }
      // Don't close if clicking inside the bug report modal (rendered via portal)
      const bugReportModal = document.querySelector('[data-bug-report-modal]');
      if (bugReportModal && bugReportModal.contains(target)) {
        return;
      }
      onClose();
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Prevent body scroll when menu is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Reset bug report state when menu closes to prevent stale state
  useEffect(() => {
    if (!isOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Ensure bug report modal is closed when menu closes
      setBugReportOpen(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const currentLevelInfo = LEVELS.find(l => l.level === level);
  const xpProgress = getXpProgress();

  // Get level icon component
  const getLevelIconComponent = (size: string = 'w-8 h-8') => {
    const iconType = getLevelIcon();
    switch (iconType) {
      case 'diamond':
        return <Diamond className={size} />;
      case 'trophy':
        return <Trophy className={size} />;
      case 'star':
        return <Star className={size} />;
      case 'award':
        return <Award className={size} />;
      default:
        return <Medal className={size} />;
    }
  };

  // Determine upgrade link based on subscription tier
  const getUpgradeInfo = () => {
    if (!user) return null;
    const tier = user.subscription_tier;
    if (tier === 'pro_plus') return null;
    if (tier === 'pro') return { label: t('profileMenu.upgradeToPro+', 'Upgrade to Pro+'), href: '/account/subscription' };
    return { label: t('profileMenu.upgradeToPro', 'Upgrade to Pro'), href: '/account/subscription' };
  };

  const upgradeInfo = getUpgradeInfo();

  const handleLogout = () => {
    logout();
    onClose();
    navigate('/');
  };

  const handleNavClick = () => {
    onClose();
  };

  const handleBugReport = () => {
    setBugReportOpen(true);
  };

  // Navigation items (user features) - used in accordion for admins
  const navigationItems: MenuItem[] = [
    { type: 'link', label: t('profileMenu.deals', 'Deals'), href: '/deals', icon: Tag },
    { type: 'link', label: t('profileMenu.watchlist', 'Watchlist'), href: '/account/watchlist', icon: Eye },
    { type: 'link', label: t('profileMenu.checklists', 'Checklists'), href: '/account/checklists', icon: ListChecks },
    { type: 'link', label: t('profileMenu.dailyChallenges', 'Daily Challenges'), href: '/account/challenges', icon: Target },
    { type: 'link', label: t('profileMenu.referFriend', 'Refer a Friend'), href: '/account/referrals', icon: Users },
    { type: 'divider' },
    { type: 'link', label: t('profileMenu.myProfile', 'My Profile'), href: '/account/profile', icon: Trophy },
    { type: 'link', label: t('profileMenu.accountSettings', 'Account Settings'), href: '/account/settings', icon: Settings },
    { type: 'link', label: t('profileMenu.notifications', 'Notifications'), href: '/account/notifications', icon: Bell },
    ...(upgradeInfo ? [{ type: 'link' as const, label: upgradeInfo.label, href: upgradeInfo.href, icon: Sparkles, highlight: true }] : []),
  ];

  // Menu items for authenticated non-admin users (flat menu)
  const authenticatedMenuItems: MenuItem[] = [
    ...navigationItems,
    { type: 'divider' },
    { type: 'button', label: t('profileMenu.help', 'Help'), action: () => { onClose(); window.Axiss?.open('helpcenter'); }, icon: HelpCircle },
    { type: 'button', label: t('profileMenu.reportBug', 'Report a Bug'), action: handleBugReport, icon: Bug },
    { type: 'divider' },
    { type: 'button', label: t('profileMenu.logout', 'Logout'), action: handleLogout, icon: LogOut, danger: true },
  ];

  // Menu items for guests
  const guestMenuItems: MenuItem[] = [
    { type: 'link', label: t('profileMenu.deals', 'Deals'), href: '/deals', icon: Tag },
    { type: 'button', label: t('profileMenu.help', 'Help'), action: () => { onClose(); window.Axiss?.open('helpcenter'); }, icon: HelpCircle },
    { type: 'divider' },
    { type: 'link', label: t('profileMenu.signIn', 'Sign In'), href: '/login', icon: LogIn, highlight: true },
  ];

  // For non-admin authenticated users, use flat menu
  const menuItems: MenuItem[] = isAuthenticated ? authenticatedMenuItems : guestMenuItems;

  // Render a menu item
  const renderMenuItem = (item: MenuItem, index: number) => {
    if (item.type === 'divider') {
      return <li key={`divider-${index}`} className="my-3 border-t border-gray-100" />;
    }

    const Icon = item.icon;
    const baseClasses = `
      flex items-center gap-3 w-full px-4 py-3 rounded-lg
      text-left text-sm font-medium
      transition-colors
    `;

    const colorClasses = item.danger
      ? 'text-red-600 hover:bg-red-50'
      : item.highlight
      ? 'text-primary-600 bg-primary-50 hover:bg-primary-100'
      : 'text-gray-700 hover:bg-gray-100';

    if (item.type === 'link') {
      return (
        <li key={item.label}>
          <Link
            to={item.href}
            onClick={handleNavClick}
            className={`${baseClasses} ${colorClasses}`}
          >
            {Icon && <Icon className="w-5 h-5 flex-shrink-0" />}
            <span>{item.label}</span>
          </Link>
        </li>
      );
    }

    if (item.type === 'button') {
      return (
        <li key={item.label}>
          <button
            onClick={item.action}
            className={`${baseClasses} ${colorClasses}`}
          >
            {Icon && <Icon className="w-5 h-5 flex-shrink-0" />}
            <span>{item.label}</span>
          </button>
        </li>
      );
    }

    return null;
  };

  // Render an admin nav item
  const renderAdminItem = (item: AdminNavItem) => {
    const Icon = item.icon;
    const isActive = isAdminItemActive(item);

    return (
      <li key={item.href}>
        <Link
          to={item.href}
          onClick={handleNavClick}
          className={`
            flex items-center gap-3 w-full px-4 py-2.5 rounded-lg
            text-left text-sm font-medium
            transition-colors
            ${isActive
              ? 'bg-primary-50 text-primary-700'
              : 'text-gray-700 hover:bg-gray-100'
            }
          `}
        >
          <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-primary-600' : 'text-gray-400'}`} />
          <span>{item.name}</span>
        </Link>
      </li>
    );
  };

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 z-40" aria-hidden="true" />

      {/* Menu panel */}
      <div
        ref={menuRef}
        className="fixed top-0 right-0 h-full w-full max-w-sm bg-white shadow-xl z-50 overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-label={t('profileMenu.ariaLabel', 'Profile menu')}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label={t('profileMenu.closeMenu', 'Close menu')}
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header section */}
        <div className="p-6 bg-gradient-to-br from-primary-600 to-primary-700 text-white">
          {isAuthenticated ? (
            <div className="flex items-center gap-4">
              {/* Level badge */}
              <div className="relative">
                <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg">
                  {getLevelIconComponent('w-8 h-8')}
                </div>
                {level > 0 && (
                  <span className="absolute -bottom-1 -right-1 w-7 h-7 flex items-center justify-center bg-yellow-400 text-yellow-900 font-bold text-sm rounded-full border-2 border-white shadow">
                    {level}
                  </span>
                )}
              </div>

              {/* Level info */}
              <div className="flex-1 min-w-0">
                <div className="text-lg font-semibold">
                  {t('profileMenu.level', 'Level {level}').replace('{level}', String(level))} - {currentLevelInfo?.title || t('profileMenu.newbie', 'Newbie')}
                </div>
                <div className="text-sm text-white/80 mt-1">
                  {t('profileMenu.xpTotal', '{xp} XP total').replace('{xp}', xp.toLocaleString())}
                </div>

                {/* XP progress bar */}
                {level < 10 && (
                  <div className="mt-2">
                    <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-yellow-400 rounded-full transition-all duration-300"
                        style={{ width: `${xpProgress.percentage}%` }}
                      />
                    </div>
                    <div className="text-xs text-white/70 mt-1">
                      {t('profileMenu.xpToNextLevel', '{current} / {required} XP to next level').replace('{current}', xpProgress.current.toLocaleString()).replace('{required}', xpProgress.required.toLocaleString())}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <Medal className="w-8 h-8" />
              </div>
              <div>
                <div className="text-lg font-semibold">{t('profileMenu.welcomeToRumio', 'Welcome to Rumio')}</div>
                <div className="text-sm text-white/80">{t('profileMenu.signInToTrack', 'Sign in to track deals')}</div>
              </div>
            </div>
          )}
        </div>

        {/* Language Selector */}
        <div className="px-4 py-3 border-b border-gray-100">
          <LanguageSelector />
        </div>

        {/* Menu items */}
        <nav className="p-4" aria-label={t('profileMenu.profileNavigation', 'Profile navigation')}>
          {/* Admin view: Tab-based sections */}
          {isAuthenticated && isAdmin ? (
            <>
              {/* Tab Bar */}
              <div className="flex border-b border-gray-200 mb-4">
                <button
                  onClick={() => setExpandedSection('navigation')}
                  className={`flex-1 py-3 text-sm font-semibold transition-colors ${
                    expandedSection === 'navigation'
                      ? 'text-primary-600 border-b-2 border-primary-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t('profileMenu.navigation', 'Navigation')}
                </button>
                {allAdminItems.length > 0 && (
                  <button
                    onClick={() => setExpandedSection('admin')}
                    className={`flex-1 py-3 text-sm font-semibold transition-colors ${
                      expandedSection === 'admin'
                        ? 'text-primary-600 border-b-2 border-primary-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {t('profileMenu.admin', 'Admin')}
                  </button>
                )}
              </div>

              {/* Tab Content */}
              {expandedSection === 'navigation' && (
                <ul className="space-y-0.5">
                  {navigationItems.map((item, index) => renderMenuItem(item, index))}
                </ul>
              )}

              {expandedSection === 'admin' && allAdminItems.length > 0 && (
                <ul className="space-y-0.5">
                  {allAdminItems.map((item) => renderAdminItem(item))}
                </ul>
              )}

              {/* Common Links - Always visible */}
              <div className="border-t border-gray-100 mt-4 pt-4">
                <ul className="space-y-0.5">
                  <li>
                    <button
                      onClick={() => { onClose(); window.Axiss?.open('helpcenter'); }}
                      className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-left text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      <HelpCircle className="w-5 h-5 flex-shrink-0 text-gray-400" />
                      <span>{t('profileMenu.help', 'Help')}</span>
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => { onClose(); window.Axiss?.open('admin'); }}
                      className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-left text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      <Settings className="w-5 h-5 flex-shrink-0 text-gray-400" />
                      <span>{t('profileMenu.manageSupport', 'Manage Support')}</span>
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={handleBugReport}
                      className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-left text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      <Bug className="w-5 h-5 flex-shrink-0 text-gray-400" />
                      <span>{t('profileMenu.reportBug', 'Report a Bug')}</span>
                    </button>
                  </li>
                </ul>
              </div>

              {/* Logout - Always visible */}
              <div className="border-t border-gray-100 mt-4 pt-4">
                <ul>
                  <li>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-left text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <LogOut className="w-5 h-5 flex-shrink-0" />
                      <span>{t('profileMenu.logout', 'Logout')}</span>
                    </button>
                  </li>
                </ul>
              </div>
            </>
          ) : (
            /* Non-admin view: Flat menu */
            <ul className="space-y-1">
              {menuItems.map((item, index) => renderMenuItem(item, index))}
            </ul>
          )}
        </nav>

        {/* User email footer (authenticated only) */}
        {isAuthenticated && user && (
          <div className="px-6 py-4 border-t border-gray-100 mt-auto">
            <div className="text-xs text-gray-500 truncate">
              {t('profileMenu.signedInAs', 'Signed in as')} <span className="font-medium text-gray-700">{user.email}</span>
            </div>
          </div>
        )}
      </div>

      {/* Bug Report Modal */}
      <BugReportModal
        isOpen={bugReportOpen}
        onClose={() => setBugReportOpen(false)}
      />
    </>,
    document.body
  );
}
